import React, { useState, useEffect, useCallback, useRef } from 'react';
import s from './style.module.scss';
import Switch from './Switch';
import cx from 'classnames';
import StrictlyNecessaryModal from './Stirctly';
import AdvertisingModal from './Advertising';
import MarketingModal from './Marketing';
import { getWithAuth, postWithAuth } from '@/util';
import { data } from './mock';

const Settings = () => {
  // 检查权限是否全部启用
  const getSumPermission = (permissionObj) => {
    return Object.values(permissionObj).every((value) => value === true);
  };

  // 获取初始权限数据
  const getInitialPermissions = async () => {
    try {
      const res = await getWithAuth('/web/account/privacy-permissions');
      const curAdvertising = res.data?.privacyPermissions?.advertising;
      const curMarketing = res.data?.privacyPermissions?.marketing;
      const afData = await getWithAuth('/web/account/af-integrations');
      const integrationsData = afData.data || data;

      // 构建advertising权限对象
      const advertising = {};
      integrationsData?.afIntegrations?.forEach((item) => {
        advertising[item.key] = curAdvertising?.[item.key] ?? curAdvertising?.default;
      });
      advertising.default = curAdvertising?.default ?? true;

      // 构建marketing权限对象（接口未返回的字段兜底为 true，与初始 state 一致）
      const marketing = {
        appsflyer: curMarketing?.appsflyer ?? curMarketing?.default ?? true,
        tiktok: curMarketing?.tiktok ?? curMarketing?.default ?? true,
        facebook: curMarketing?.facebook ?? curMarketing?.default ?? true,
        default: curMarketing?.default ?? true,
      };

      setPermissions({
        strictlyNecessary: true, // Always true and disabled
        advertising,
        marketing,
      });
    } catch (error) {
      console.error('Failed to get permissions:', error);
    }
  };

  const [permissions, setPermissions] = useState({
    strictlyNecessary: true, // Always true and disabled
    advertising: {
      default: true,
    },
    marketing: {
      appsflyer: true,
      tiktok: true,
      facebook: true,
      default: true,
    },
  });
  const permissionsRef = useRef(permissions);
  const [detailModal, setDetailModal] = useState();

  // 同步更新 ref
  useEffect(() => {
    permissionsRef.current = permissions;
    // getSumPermission(permissions.advertising);
  }, [permissions]);

  useEffect(() => {
    getInitialPermissions();
  }, []);

  const onClose = useCallback(async () => {
    const currentPermissions = permissionsRef.current;
    await postWithAuth('/web/account/privacy-permissions', currentPermissions);
    window?.nativeBridge && window.nativeBridge.callNativeFunction('goBack', null);
  }, []);

  const handleToggle = (permissionType, checked) => {
    const currentPermission = permissionsRef.current[permissionType];
    const updatedPermission = { ...currentPermission };

    // 更新除default外的所有权限
    Object.keys(updatedPermission).forEach((key) => {
      updatedPermission[key] = checked;
    });

    setPermissions((prev) => ({
      ...prev,
      [permissionType]: updatedPermission,
    }));
  };

  const handleAllowAll = useCallback(async () => {
    const currentPermissions = permissionsRef.current;

    // 创建新的权限对象，所有值设为true
    const newAdvertising = Object.keys(currentPermissions.advertising).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});

    const newMarketing = Object.keys(currentPermissions.marketing).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});

    setPermissions((prev) => ({
      ...prev,
      advertising: newAdvertising,
      marketing: newMarketing,
    }));

    setTimeout(() => {
      onClose();
    }, 1000);
  }, [onClose]);

  const handleRefuseAll = useCallback(async () => {
    const currentPermissions = permissionsRef.current;

    // 创建新的权限对象，所有值设为false
    const newAdvertising = Object.keys(currentPermissions.advertising).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});

    const newMarketing = Object.keys(currentPermissions.marketing).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});

    setPermissions((prev) => ({
      ...prev,
      advertising: newAdvertising,
      marketing: newMarketing,
    }));

    setTimeout(() => {
      onClose();
    }, 1000);
  }, [onClose]);

  const ModalMap = {
    strictlyNecessary: <StrictlyNecessaryModal setDetailModal={setDetailModal} />,
    advertising: (
      <AdvertisingModal
        setDetailModal={setDetailModal}
        permissions={permissions}
        setPermissions={setPermissions}
        permissionsRef={permissionsRef}
      />
    ),
    marketing: (
      <MarketingModal
        setDetailModal={setDetailModal}
        permissions={permissions}
        setPermissions={setPermissions}
        permissionsRef={permissionsRef}
      />
    ),
  };

  return (
    <div className={cx('news-detail-md', s.settings)}>
      {detailModal ? (
        ModalMap[detailModal]
      ) : (
        <>
          <div className={s.settings__text}>
            Like other apps, using Duet involves the storing and retrieval of information from your device, through
            trackers operated by us or our partners. We provide below a simple tool allowing you to tailor your choices
            as you deem fit in relation to these trackers. You can change your choices at any time in your Settings.
          </div>
          <h2>MANAGE PERMISSIONS</h2>
          <div className={s.settings__item}>
            <div>Strictly Necessary Permissions</div>
            <Switch disabled checked />
          </div>
          <div className={s.settings_text}>
            These tools are necessary for the app to function and thus cannot be switched off. They are usually set in
            response to actions made by you which amount to a request for services, such as setting your privacy
            preferences, logging in or filling in forms.
            <span className={s.button} onClick={() => setDetailModal('strictlyNecessary')}>
              View Details
            </span>
          </div>

          {/* Advertising */}
          <div className={s.settings__item}>
            <div>
              <div>Allow Advertising Permissions</div>
              <div className={s.button}>
                I am allowing Advertising Permissions. New Advertising Permissions will be enabled by default.
              </div>
            </div>
            <Switch
              checked={getSumPermission(permissions.advertising)}
              onChange={(checked) => handleToggle('advertising', checked)}
            />
          </div>
          <div className={cx(s.settings__item, s.merge)} onClick={() => setDetailModal('advertising')}>
            <div>Personalize Advertising Permissions</div>
            <div className={s.arrow} />
          </div>
          <div className={s.settings__text}>
            These tools are used to measure the effectiveness of advertising campaigns and make advertising more
            relevant to you. They perform functions like preventing the same ad from continuously reappearing, ensuring
            that ads are properly displayed, selecting advertisements based on your profile and measuring the number of
            ads displayed and their performance, such as how many people clicked on a given ad. If you do not allow
            these tools, the ads will be less relevant to you. These tools do not involve the sharing of unique
            identifiers or other data that would allow third parties to track you across apps and websites.
          </div>

          {/* Marketing */}
          <div className={s.settings__item}>
            <div>
              <div>Allow Marketing Permissions</div>
              <div className={s.button}>
                I am allowing Marketing Permissions. New Marketing Permissions will be enabled by default.
              </div>
            </div>
            <Switch
              checked={getSumPermission(permissions.marketing)}
              onChange={(checked) => handleToggle('marketing', checked)}
            />
          </div>
          <div className={cx(s.settings__item, s.merge)} onClick={() => setDetailModal('marketing')}>
            <div>Personalize Marketing Permissions</div>
            <div className={s.arrow} />
          </div>
          <div className={s.settings__text}>
            These tools allow us to count visits and traffic sources and understand how users move around the app. This
            in turn helps us assess how the app performs and which features are the most and least popular so we can
            continue improving your experience.
          </div>
        </>
      )}

      {!detailModal && (
        <div className={s.settings__buttons}>
          <div className={s.settings__button} onClick={handleAllowAll}>
            Allow all
          </div>
          <div className={s.settings__button} onClick={handleRefuseAll}>
            Refuse all
          </div>
          <div className={s.settings__button} onClick={onClose}>
            Save
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
